import {
  type ActionData,
  REFERRAL_REWARD_STATUSES,
  type ReferralReward,
  type ReferralRewardStatus,
} from '@contract';
import { useMutation } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { createColumnHelper } from '@tanstack/react-table';
import { useState } from 'react';
import { z } from 'zod';
import { DataTable } from '@/components/data-table';
import { ExportButton } from '@/components/export-button';
import { PageHeader } from '@/components/page-header';
import { ProposeChange } from '@/components/propose-change';
import { ReasonDialog } from '@/components/reason-dialog';
import { StatusBadge } from '@/components/record-table';
import { useOpenRecord } from '@/components/sheet/use-sheet';
import { Alert } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { LinkButton } from '@/components/ui/link-button';
import { Select } from '@/components/ui/select';
import { callAdmin } from '@/lib/api';
import { formatDateTime } from '@/lib/format';
import { invalidate, useKeysetList } from '@/lib/query';
import {
  budgetMeter,
  capText,
  isWaiting,
  ROLE_LABEL,
  rewardAmountText,
} from '@/lib/referral-rewards';
import { useCan } from '@/lib/staff-context';
import { toast } from '@/lib/toast';

const ROLES = ['referrer', 'referred', 'bonus'] as const;

const searchSchema = z.object({
  status: z.enum(REFERRAL_REWARD_STATUSES).optional(),
  role: z.enum(ROLES).optional(),
  beneficiary: z.uuid().optional(),
});

/** Filters live in the URL, so a filtered view can be shared (decision 0007). */
export const Route = createFileRoute('/_app/referral-rewards')({
  validateSearch: searchSchema,
  component: ReferralRewardsPage,
});

const column = createColumnHelper<ReferralReward>();

/** The table's columns; `onShowCap` opens one customer's cap view. */
function rewardColumns(onShowCap: (beneficiary: string) => unknown) {
  return [
    column.display({
      id: 'customer',
      header: 'Customer',
      cell: (info) => <CustomerCell reward={info.row.original} />,
    }),
    column.display({
      id: 'reward',
      meta: { sortValue: (reward) => reward.amount_minor },
      header: 'Reward',
      cell: (info) => (
        <div>
          <span className="block font-medium tabular-nums">
            {rewardAmountText(info.row.original)}
          </span>
          <span className="block text-xs text-muted-foreground">
            {ROLE_LABEL[info.row.original.role]}
          </span>
        </div>
      ),
    }),
    column.display({
      id: 'status',
      meta: { sortValue: (reward) => reward.status },
      header: 'Status',
      cell: (info) => <StatusCell reward={info.row.original} />,
    }),
    column.display({
      id: 'created',
      meta: { sortValue: (reward) => reward.created_at },
      header: 'Earned',
      cell: (info) => (
        <span className="whitespace-nowrap text-xs">
          {formatDateTime(info.row.original.created_at)}
        </span>
      ),
    }),
    column.display({
      id: 'actions',
      header: 'Actions',
      cell: (info) => (
        <RewardActions reward={info.row.original} onShowCap={onShowCap} />
      ),
    }),
  ];
}

function ReferralRewardsPage() {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const openRecord = useOpenRecord();
  const list = useKeysetList(
    'referral_rewards.list',
    {
      status: search.status,
      role: search.role,
      beneficiary_id: search.beneficiary,
      limit: 50,
    },
    { rows: (data) => data.rows },
  );

  const columns = rewardColumns((beneficiary) =>
    navigate({ search: { ...search, beneficiary } }),
  );

  return (
    <>
      <PageHeader
        title="Referral rewards"
        description="What successful referrals earned. When approval is on, one person proposes paying a reward and someone else approves it from Changes; declining needs only a reason. Rewards are paid from Sao's own funds."
        actions={
          <ExportButton
            source="referral_rewards.list"
            args={{
              status: search.status,
              role: search.role,
              beneficiary_id: search.beneficiary,
            }}
          />
        }
      />
      {list.error ? (
        <Alert variant="destructive">{list.error.message}</Alert>
      ) : null}
      {list.data ? <Summary data={list.data} /> : null}
      <div className="grid grid-cols-2 items-end gap-3 sm:flex sm:flex-wrap">
        <Select
          label="Status"
          className="w-full sm:w-52"
          value={search.status ?? ''}
          onChange={(event) =>
            void navigate({
              search: {
                ...search,
                status: (event.target.value || undefined) as
                  | ReferralRewardStatus
                  | undefined,
              },
            })
          }
        >
          <option value="">Any</option>
          {REFERRAL_REWARD_STATUSES.map((status) => (
            <option key={status} value={status}>
              {status.replaceAll('_', ' ')}
            </option>
          ))}
        </Select>
        <Select
          label="Reward for"
          className="w-full sm:w-44"
          value={search.role ?? ''}
          onChange={(event) =>
            void navigate({
              search: {
                ...search,
                role: (event.target.value || undefined) as
                  | (typeof ROLES)[number]
                  | undefined,
              },
            })
          }
        >
          <option value="">Anyone</option>
          {ROLES.map((role) => (
            <option key={role} value={role}>
              {ROLE_LABEL[role]}
            </option>
          ))}
        </Select>
        {search.beneficiary ? (
          <Button
            variant="ghost"
            onClick={() =>
              void navigate({
                search: {
                  ...search,
                  beneficiary: undefined,
                },
              })
            }
          >
            Show everyone
          </Button>
        ) : null}
      </div>
      <DataTable
        columns={columns}
        data={list.rows}
        more={list.more}
        empty="No rewards match these filters."
        rowLabel={(row) =>
          row.beneficiary
            ? `Open ${row.beneficiary.name ?? 'the customer'}`
            : undefined
        }
        onRowClick={(row, rowEl) => {
          if (row.beneficiary)
            openRecord({ type: 'cust', id: row.beneficiary.id }, rowEl);
        }}
      />
    </>
  );
}

/** One line on whether rewards are on and how they are paid. */
function rewardsNote(summary: {
  reward_kind: string;
  need_approval: boolean;
}): string {
  if (summary.reward_kind === 'none') {
    return 'Rewards are off: nothing accrues or is paid.';
  }
  const kind = summary.reward_kind.replaceAll('_', ' ');
  const how = summary.need_approval
    ? 'and wait for approval'
    : 'paid without approval';
  return `Rewards are ${kind}, ${how}.`;
}

function Summary({
  data,
}: Readonly<{ data: ActionData<'referral_rewards.list'> }>) {
  const { summary, cap } = data;
  const meter = budgetMeter(summary);
  return (
    <div className="grid gap-3 lg:grid-cols-2">
      <Card>
        <CardHeader>
          <CardTitle>This month&apos;s budget</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-2">
          <p className="text-sm">{meter.text}</p>
          {meter.percent === null ? null : (
            <progress
              className={`h-2 w-full ${METER_TONE[meter.tone]}`}
              max={100}
              value={meter.percent}
              aria-label="Share of the monthly budget used"
            />
          )}
          <p className="text-xs text-muted-foreground">
            {rewardsNote(summary)}
          </p>
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <CardTitle>{cap ? "This customer's cap" : 'By status'}</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-wrap gap-2">
          {cap ? (
            <p className="text-sm">{capText(cap)}</p>
          ) : (
            REFERRAL_REWARD_STATUSES.map((status) => (
              <span key={status} className="flex items-center gap-1 text-sm">
                <StatusBadge status={status} />
                {summary.counts[status] ?? 0}
              </span>
            ))
          )}
        </CardContent>
      </Card>
    </div>
  );
}

const METER_TONE = {
  ok: 'accent-emerald-600',
  warning: 'accent-amber-500',
  full: 'accent-red-600',
} as const;

function CustomerCell({ reward }: Readonly<{ reward: ReferralReward }>) {
  if (!reward.beneficiary)
    return <span className="text-muted-foreground">Gone</span>;
  return (
    <div>
      <span className="block whitespace-nowrap">
        {reward.beneficiary.name ?? 'No name yet'}
      </span>
      <span className="block whitespace-nowrap font-mono text-xs text-muted-foreground">
        {reward.beneficiary.phone ?? ''}
      </span>
    </div>
  );
}

function StatusCell({ reward }: Readonly<{ reward: ReferralReward }>) {
  return (
    <div className="flex flex-col items-start gap-1">
      <StatusBadge status={reward.status} />
      {reward.needs_review ? (
        <Badge variant="warning">referral voided after payment</Badge>
      ) : null}
      {reward.reason ? (
        <span className="text-xs text-muted-foreground">{reward.reason}</span>
      ) : null}
    </div>
  );
}

function RewardActions({
  reward,
  onShowCap,
}: Readonly<{
  reward: ReferralReward;
  onShowCap: (beneficiary: string) => void;
}>) {
  const beneficiary = reward.beneficiary?.id;
  return (
    <div className="flex flex-wrap items-center gap-2">
      {isWaiting(reward) ? <WaitingActions reward={reward} /> : null}
      {beneficiary ? (
        <Button
          size="sm"
          variant="ghost"
          onClick={() => onShowCap(beneficiary)}
        >
          Cap
        </Button>
      ) : null}
    </div>
  );
}

function WaitingActions({ reward }: Readonly<{ reward: ReferralReward }>) {
  if (reward.change_request_id) {
    return (
      <LinkButton
        to="/approvals/$requestId"
        params={{ requestId: reward.change_request_id }}
      >
        Waiting for a checker
      </LinkButton>
    );
  }
  return (
    <>
      <ProposeChange
        kind="referral_reward"
        payload={{ reward_id: reward.id }}
        label="Approve"
        title="Approve this reward"
        description="Someone else confirms it from Changes, then it is paid from Sao's own funds."
        variant="default"
        onDone={() => void invalidate('referral_rewards.list')}
      />
      <Decline reward={reward} />
    </>
  );
}

function Decline({ reward }: Readonly<{ reward: ReferralReward }>) {
  const [open, setOpen] = useState(false);
  const decline = useMutation({
    mutationFn: (reason: string) =>
      callAdmin('referral_rewards.decline', { reward_id: reward.id, reason }),
    onSuccess: async () => {
      setOpen(false);
      toast.success('Reward declined.');
      await invalidate('referral_rewards.list');
    },
    onError: () => invalidate('referral_rewards.list'),
  });
  if (!useCan('referral_rewards.decline')) return null;
  return (
    <>
      <Button size="sm" variant="outline" onClick={() => setOpen(true)}>
        Decline
      </Button>
      <ReasonDialog
        open={open}
        onOpenChange={setOpen}
        title="Decline this reward"
        description="The customer is not paid. The reason is kept in the audit log."
        confirmLabel="Decline"
        pending={decline.isPending}
        error={decline.error?.message ?? null}
        onConfirm={(reason) => decline.mutate(reason)}
      />
    </>
  );
}
